<template lang="pug">
//- FORM
form.likert-question.w-full.text-lg.dont-unlock.break-inside-avoid(
  :class="{ '-is-submitted': isSubmitted }"
  :data-completed="isSubmitted"
  :data-id="screen.id"
  :id="`q_${screen.id}`"
  ref="el"
  tabindex="-1"
)
  //- QUESTION TEXT
  .likert-question__text.prose-stack.mb-8.fade-in
    uc-text.font-bold(
      :k="`${screen.id}.question`"
      :text="screen.question"
    )

  uc-preload-sfx(name="click1")

  fieldset.fade-in(
    :disabled="isSubmitted"
    :aria-label="`Likert scale question: ${screen.question}`"
    role="radiogroup"
    v-if="!audioDoc && isValidLikertData"
  )
    ul.question__options.flex.flex-col.md_grid.max-w-prose.md_max-w-none.bg-paper.surface.corners.overflow-hidden(
      :style="{ gridTemplateColumns: `repeat(${likertOptions?.length}, minmax(80px, 1fr))` }"
      role="none"
    )
      li.question-option.ripple.flex(
        :class="{ 'bg-primary-200/50 dark_bg-primary-700/20': isOptionSelected(optionIndex) }"
        v-for="(option, optionIndex) in likertOptions"
      )
        label.transition.flex.flex-col.flex-1.w-full.h-full.p-4(
          :class="{ 'cursor-pointer': !isSubmitted }"
          :for="getOptionId(optionIndex)"
        )
          .ripple__wave.pointer-events-none(
            class="bg-primary-500/10"
          )
          .question-option__background.fill.transition.pointer-events-none
          input.sr-only.peer(
            :checked="isOptionSelected(optionIndex)"
            data-sfx="click1"
            :disabled="isSubmitted"
            :id="getOptionId(optionIndex)"
            :key="getOptionId(optionIndex)"
            :data-index="`${optionIndex}`"
            :value="option.value"
            :name="`${screen.id}_likert`"
            :aria-describedby="`${getOptionId(optionIndex)}_desc`"
            @change="changeHandler"
            type="radio"
            uc-word-skip=""
          )
          uc-text.sr-only(
            text="selected"
            v-if="isSubmitted && isOptionSelected(optionIndex)"
          )
          .flex.flex-1.flex-row.items-center.gap-2.md_gap-4.md_flex-col.md_justify-center.md_flex-col-reverse.peer-focus-visible_ring-4
            .question-option__radio.transition.w-4.aspect-square.rounded-full.inline-flex(
              class="border-primary-700/70 text-primary-700 dark_border-primary-400/80 dark_text-primary-400 m-1 h-4 w-4 items-center justify-center border-[1.5px] text-sm"
              :class="[{ 'opacity-30': isSubmitted }]"
            )
              transition-pop(:duration="0.15")
                .inline-flex.items-center.justify-center(
                  v-if="isOptionSelected(optionIndex)"
                )
                  uc-icon.scale-75(
                    :active="isOptionSelected(optionIndex)"
                    icon="mdi:circle"
                  )

            .question-option__text.prose-stack.flex.flex-none.md_flex-1
              uc-text.md_text-center.w-full(
                :class="[isOptionSelected(optionIndex) ? 'font-semibold px-0' : 'px-1']"
                :k="`${getOptionId(optionIndex)}`"
                :text="hasTranslation(option.text)"
                :id="`${getOptionId(optionIndex)}_desc`"
              )
  //- This is a debug block to show the likert-data errors
  .text-red-500.font-bold.italic.border-red-500.border-2.p-4(
    uc-dont-read=""
    uc-word-skip=""
    v-else-if="!isValidLikertData"
  )
    uc-text(
      :text="`Missing or incorrect likert-data found for screen: ${props.screen.id}.`"
      type="p"
    )
    uc-text(
      :text="`score-direction must be one of: ${LIKERT_SCORE_DIRECTIONS.join(', ')}`"
      type="p"
    )
    uc-text(
      :text="`scale must be one of: ${Object.keys(LIKERT_SCALES).join(', ')}`"
      type="p"
    )

  //- SUBMIT BUTTON
  .likert-question__submit-wrapper.fade-in
    .likert-question__submit-btn.flex.justify-end.items-center.mt-8.group(
      :class="[{ 'hidden': isSubmitted }, smallDevice && 'flex-wrap gap-2']"
    )
      uc-preload-sfx(name="survey-submit")
      uc-button.likert-question__submit.btn.btn-primary.ripple(
        :class="smallDevice && 'w-full'"
        :disabled="disableSubmit"
        ref="submit"
        @click="submitHandler"
        data-sfx="survey-submit"
      )
        span.ripple__wave
        uc-text(text="Submit")

  //- FEEDBACK TEXT
  .likert-question__feedback.flex-1.mt-8.fade-in(
    ref="feedbackRef"
    aria-live="polite"
    aria-atomic="true"
    role="status"
    v-if="feedback"
  )
    audio-marker(:name="`${screen.id}-feedback`")

    transition-expand(
      :delay="0.5"
      :duration="1"
      scroll=""
    )
      .likert-question__feedback-content.block.outline-2(
        v-if="isSubmitted || wordDoc || audioDoc"
      )
        uc-text.prose.dark_prose-invert(
          :k="`${screen.id}.feedback`"
          :text="feedback"
          type="p"
        )
        slot
</template>

<script lang="ts">
export default {
  inheritAttrs: false
};
</script>

<script setup lang="ts">
import { nextTick, ref, computed, unref, inject, watch, toRef } from 'vue';
import { set, setCompleted } from '../lib/SCORM';
import gsap from 'gsap';
import useSuspend from '../lib/useSuspend';
import useScreenAudio from '../lib/useScreenAudio';
import likertQuestionSchema from '../lib/screen-schemas/screen-likert-question.schema';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { saveInsightsData } from '../lib/insightsData';
import useTranslations from '../stores/useTranslations';
import isQuizPage from '../lib/isQuizPage';

import type {
  LikertScale,
  LikertOption,
  LikertScoreDirection
} from '../../types/ScreenLikertQuestion';
import { stop } from '../lib/audio';

const audioDoc = inject('audioDoc') as boolean;
const wordDoc = unref(inject('wordDoc')) as boolean;
const pageId = inject('pageId') as object;
const sectionId = inject('sectionId') as string;
const isKitchenSink = inject<Ref<boolean>>('isKitchenSink');
const isEditModeActive = inject('isEditModeActive') as Ref<boolean>;
const props = defineProps<{ editPath: string; screen: ScreenLikertQuestion }>();
const suspend = useSuspend();
const { playSlot } = useScreenAudio(
  toRef(props, 'screen'),
  likertQuestionSchema
);
const breakpoints = useBreakpoints(breakpointsTailwind);
const smallDevice = breakpoints.smallerOrEqual('sm');
const largeDevice = breakpoints.greaterOrEqual('lg');
const submit = ref<HTMLElement>();
const submittedState = ref(false);
const el = ref<HTMLElement>();
const { translations } = useTranslations();

function hasTranslation(key: string | number, fallback: string | number = key) {
  // get the translation if it exists
  let maybeTranslated =
    translations?.[suspend.locale]?.[key] ||
    translations?.[suspend.locale]?.[fallback];

  return maybeTranslated || key || fallback;
}

// expose these strings for framework translations
// text="Strongly Disagree"
// text="Disagree"
// text="Neutral"
// text="Agree"
// text="Strongly Agree"

// text="Not at all aware"
// text="Slightly aware"
// text="Moderately aware"
// text="Very aware"
// text="Extremely aware"

// text="Never"
// text="Rarely"
// text="Sometimes"
// text="Often"
// text="Always"

// text="Very unlikely"
// text="Unlikely"
// text="Neutral"
// text="Likely"
// text="Very likely"

// Pre-defined Likert scales
const LIKERT_SCALES: Record<LikertScale, LikertOption[]> = {
  agreement: [
    { text: 'Strongly Disagree', value: 1 },
    { text: 'Disagree', value: 2 },
    { text: 'Neutral', value: 3 },
    { text: 'Agree', value: 4 },
    { text: 'Strongly Agree', value: 5 }
  ],
  awareness: [
    { text: 'Not at all aware', value: 1 },
    { text: 'Slightly aware', value: 2 },
    { text: 'Moderately aware', value: 3 },
    { text: 'Very aware', value: 4 },
    { text: 'Extremely aware', value: 5 }
  ],
  frequency: [
    { text: 'Never', value: 1 },
    { text: 'Rarely', value: 2 },
    { text: 'Sometimes', value: 3 },
    { text: 'Often', value: 4 },
    { text: 'Always', value: 5 }
  ],
  likelihood: [
    { text: 'Very unlikely', value: 1 },
    { text: 'Unlikely', value: 2 },
    { text: 'Neutral', value: 3 },
    { text: 'Likely', value: 4 },
    { text: 'Very likely', value: 5 }
  ]
};

const LIKERT_SCORE_DIRECTIONS: LikertScoreDirection[] = ['normal', 'reverse'];

const isValidLikertData = computed<boolean>(() => {
  return (
    props.screen['likert-data'] &&
    props.screen['likert-data'].scale &&
    props.screen['likert-data']['score-direction'] &&
    LIKERT_SCORE_DIRECTIONS.includes(
      props.screen['likert-data']['score-direction']
    ) &&
    Object.keys(LIKERT_SCALES).includes(props.screen['likert-data'].scale)
  );
});

// validate the likert-data, which is mandatory
const likertScale = computed(() => {
  return props.screen['likert-data']?.scale;
});

// Get the appropriate Likert options based on the selected scale
const likertOptions = computed(() => {
  return LIKERT_SCALES[likertScale.value];
});

const suspendQuestion = computed(() => {
  return suspend.likertQuestions[props.screen.id] || {};
});

// load from suspend or default to -1
const selectedIndex = ref(suspendQuestion.value.selectedIndex ?? -1);

const isSubmitted = computed(() => {
  return Boolean(suspend.completed[props.screen.id]);
});

const isReadyToSubmit = computed(() => {
  return selectedIndex.value !== -1;
});

const disableSubmit = computed(() => {
  return !isReadyToSubmit.value || isSubmitted.value;
});

const feedback = computed(() => props.screen.feedback);

function getOptionId(optionIndex: number) {
  return `${props.screen.id}_o${optionIndex}`;
}

function isOptionSelected(optionIndex: number) {
  return selectedIndex.value === parseInt(optionIndex);
}

// only used in the KS
function resetQuestion() {
  submittedState.value = false;
  delete suspend.likertQuestions[props.screen.id];
  delete suspend.insightsData[props.screen.id];

  if (isKitchenSink?.value) el.value?.focus();
}

// the KS has a reset screen button, so listen for the resetting of the screen's completion
watch(
  () => suspend.completed[props.screen.id],
  (newVal, oldVal) => {
    if (oldVal && !newVal) {
      resetQuestion();
    }
  }
);

function changeHandler(e: Event & { target: HTMLInputElement }) {
  if (e.target.checked) {
    selectedIndex.value = parseInt(e.target.getAttribute('data-index')) ?? -1;
  }
}

function delayNextButton(delay) {
  const section = el.value?.closest('.section') as HTMLElement;
  const nextButton = section?.querySelector(
    '.screen-next-button'
  ) as HTMLElement;

  if (nextButton)
    nextButton.style.transitionDelay = suspend.reducedMotion
      ? '0s'
      : `${delay}s`;
}

async function submitHandler(e: Event) {
  stop();

  submittedState.value = true;

  const { id } = props.screen;

  // we need to apply the delay before completion as it relies on a transtion-delay for the auto-alpha-1 CSS class
  delayNextButton(feedback.value ? 1 : 0.5);

  setCompleted(id);

  await nextTick();

  await new Promise(r => gsap.delayedCall(props.screen.feedback ? 0.5 : 0, r));

  setCompleted(sectionId);

  saveLikertData();

  const scoreDirection = props.screen['likert-data']?.['score-direction'];
  saveInsightsData(props.screen, {
    'likert-value': getLikertScore(
      likertOptions.value[selectedIndex.value].value,
      scoreDirection === 'reverse'
    ),
    'likert-score-direction': scoreDirection
  });

  playFeedbackAudio();
}

function saveLikertData() {
  const obj: Suspend['likertQuestions'][string] = {
    selectedIndex: selectedIndex.value,
    scoreDirection: props.screen['likert-data']?.['score-direction'],
    scale: likertScale.value
  };
  set(`likertQuestions.${props.screen.id}`, obj);
}

// get the score for the likert question based on the value for the selected option and the score direction
function getLikertScore(responseValue: number, reverseScored: boolean) {
  const MAX = 5;
  const MIN = 1;

  return reverseScored ? MAX + MIN - responseValue : responseValue;
}

function playFeedbackAudio() {
  playSlot('feedback');
}

// after quiz reset allow them to change their answer and resubmit
function resetLikertQuestion() {
  selectedIndex.value = -1;
  submittedState.value = false;
  set(`completed.${props.screen.id}`, false);
}

const app = document.querySelector('#app');
app?.addEventListener('quizReset', async e => {
  if (await isQuizPage(e as CustomEvent, pageId)) resetLikertQuestion();
});
</script>

<style scoped>
fieldset:not(:disabled) li.question-option:hover,
li.question-option:has(input:focus-visible) .question-option__background.fill {
  @apply bg-primary-600/10 dark_bg-primary-600/10;
}
</style>
